import { ReactNode, useMemo } from 'react';

import { ApiError } from '@libs/errors';
import { usePageForgotten } from '@libs/hooks/usePageForgotten';

import { useAnonymousSecret } from '@features/auth/hooks/useAnonymousSecret';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useAnonymousGameToken } from '@features/game/hooks/useAnonymousGameToken';
import { createStubFastGame } from '@features/game/utils/createStubFastGame';
import {
  useAcceptInviteMutation,
  useDeleteInviteMutation,
  useFriendGamesSocket,
  useInviteQuery,
  useMyInvitesQuery,
  useRejectInviteMutation,
  useRematchInviteMutation,
} from '@features/shared/api/fastgames';
import { useDeviceId } from '@features/shared/hooks/useDeviceId';

import { FriendGameContext } from './FriendGameContext';
import { IFriendGameContext } from './types';

const PAGE_FORGOTTEN_TIMEOUT = 10 * 60 * 1000;

/*
    ____,-------------------------------,____
    \   |         Play Provider         |   /
    /___|-------------------------------|___\
*/

export function FriendGameProvider({
  inviteId,
  children,
}: {
  children: ReactNode;
  inviteId: string;
}) {
  const deviceId = useDeviceId();
  const { isAuthenticated, user } = useAuth();
  const anonymousSecret = useAnonymousSecret(isAuthenticated);

  // ---- Данные ----

  const myInvitesQuery = useMyInvitesQuery(deviceId, anonymousSecret, {
    refetchOnWindowFocus: false,
  });
  const inviteQuery = useInviteQuery({
    inviteId,
    deviceId,
    anonymousSecret,
    staleTime: 100, // sic! стараемся использовать кэш myInvites и createInvite
    refetchOnWindowFocus: false,
    enabled: !myInvitesQuery.isPending, // сначала поищем в активных (кэш myInvites)
  });

  const activeInvite = inviteQuery.data;
  const inviteError = inviteQuery.error instanceof ApiError ? inviteQuery.error : undefined;
  const status = activeInvite?.status;
  const canRematch = status === 'RematchWaiting' || status === 'RematchRequested';

  const activeGameId =
    status === 'Playing' || canRematch || status === 'Finished' ? inviteId : undefined;

  const rematchExpiry = canRematch ? activeInvite!.expireTimestampMs : undefined;
  const rematchSide = activeInvite?.rematchInitiatorSide;

  const anonymousId = myInvitesQuery.data?.anonymousId
    ? myInvitesQuery.data.anonymousId.startsWith('anon:')
      ? myInvitesQuery.data.anonymousId
      : `anon:${myInvitesQuery.data.anonymousId}`
    : null;

  const {
    customAccessToken,
    isPending: isTokenPending,
    isError: isTokenError,
  } = useAnonymousGameToken(activeGameId, deviceId, anonymousSecret);

  // ---- Действия ----

  const acceptMutation = useAcceptInviteMutation(deviceId, anonymousSecret);
  const rejectMutation = useRejectInviteMutation(deviceId, anonymousSecret);
  const rematchMutation = useRematchInviteMutation(deviceId, anonymousSecret);
  const deleteMutation = useDeleteInviteMutation(deviceId, anonymousSecret);

  // ---- Сокет ----

  const pageForgotten = usePageForgotten(PAGE_FORGOTTEN_TIMEOUT);

  useFriendGamesSocket({
    deviceId,
    anonymousSecret,
    enabled: Boolean(
      !pageForgotten &&
      activeInvite &&
      activeInvite.status !== 'Finished' &&
      activeInvite.status !== 'Expired' &&
      activeInvite.status !== 'Deleted',
    ),
  });

  // ---- Стор ----

  const game = useMemo((): IFriendGameContext['game'] | undefined => {
    const reject = rejectMutation.mutate;
    const rematch = rematchMutation.mutate;
    return activeGameId
      ? {
          gameId: activeGameId,
          isLoading: isTokenPending,
          isFinished: status !== 'Playing',
          customToken: customAccessToken,
          customTokenError: isTokenError,
          //
          rematchExpiration: rematchExpiry ?? undefined,
          rematchWhite: rematchSide === 'White',
          rematchBlack: rematchSide === 'Black',
          //
          reject: () => reject(inviteId),
          rematch: () => rematch(inviteId),
        }
      : undefined;
  }, [
    activeGameId,
    customAccessToken,
    isTokenError,
    inviteId,
    isTokenPending,
    rematchExpiry,
    rejectMutation.mutate,
    rematchMutation.mutate,
    rematchSide,
    status,
  ]);

  const store = useMemo<IFriendGameContext>(
    () => ({
      isLoading: myInvitesQuery.isPending || inviteQuery.isPending,
      inviteId,
      playerId: user?.userId ?? anonymousId ?? undefined,
      error: inviteError,
      game,
      invite: activeInvite,
      myGamesCount: myInvitesQuery.data?.invites.length,
      //
      accept: () => acceptMutation.mutateAsync(inviteId),
      delete: () => deleteMutation.mutateAsync(inviteId),
    }),
    [
      acceptMutation,
      activeInvite,
      anonymousId,
      deleteMutation,
      game,
      inviteError,
      inviteId,
      inviteQuery.isPending,
      myInvitesQuery.isPending,
      myInvitesQuery.data?.invites.length,
      user?.userId,
    ],
  );

  return <FriendGameContext.Provider value={store}>{children}</FriendGameContext.Provider>;
}

/*
    ____,-------------------------------,____
    \   |         View Provider         |   /
    /___|-------------------------------|___\
*/

export function FriendGameViewProvider({
  inviteId,
  children,
}: {
  children: ReactNode;
  inviteId: string;
}) {
  const { user } = useAuth();
  const activeGameId = inviteId;

  // ---- Стор ----

  const game = useMemo(
    (): IFriendGameContext['game'] | undefined =>
      activeGameId ? createStubFastGame(activeGameId) : undefined,
    [activeGameId],
  );

  const store = useMemo<IFriendGameContext>(
    () => ({
      isLoading: false,
      inviteId,
      playerId: user?.userId ?? 'anonymous',
      game,
      //
      accept: Promise.resolve,
      delete: Promise.resolve,
    }),
    [game, inviteId, user?.userId],
  );

  return <FriendGameContext.Provider value={store}>{children}</FriendGameContext.Provider>;
}
